/**
 * Cookie Banner Accessibility
 * Auto-enhances DPOEasy cookie banner with ARIA and keyboard support
 */

import BaseA11yEnhancer from './base-a11y-enhancer';

const LABELS = {
    'Gerenciar cookies': 'Gerenciar preferências de cookies',
    'Rejeitar todos': 'Rejeitar todos os cookies não essenciais',
    'Aceitar todos': 'Aceitar todos os cookies',
};

const ACCESSIBLE_COLORS = {
    heading: '#1a1a1a',
    text: '#333333',
    buttonText: '#1a1a1a',
    buttonBorder: '#ffc107',
    buttonHover: '#2b2b2b',
};

new BaseA11yEnhancer({
    selector: '.DPOEasy-MuiPaper-root',
    observeDOM: true,
    delay: 500,

    enhance: (paper) => {
        Object.assign(paper, { role: 'dialog', 'aria-modal': 'true', tabIndex: -1 });

        paper.style.cssText = `
            background-color: #ffffff !important;
            color: ${ACCESSIBLE_COLORS.text} !important;
        `;

        const h2 = paper.querySelector('h2');
        if (h2) {
            h2.id ||= 'cookie-title';
            paper.setAttribute('aria-labelledby', h2.id);
            h2.style.color = `${ACCESSIBLE_COLORS.heading} !important`;
        }

        const desc = paper.querySelector('.dpoeasy-typography-body2');
        if (desc) {
            desc.id ||= 'cookie-desc';
            desc.style.color = `${ACCESSIBLE_COLORS.text} !important`;
        }

        paper.querySelectorAll('[role="button"]').forEach((btn) => {
            const text = btn.textContent.trim();
            const label = LABELS[text];

            if (label && !btn.getAttribute('aria-label')) {
                btn.setAttribute('aria-label', label);
                if (text === 'Gerenciar cookies') btn.setAttribute('aria-describedby', 'cookie-desc');
            }

            switch (text) {
                case 'Gerenciar cookies':
                    btn.style.cssText = `
                        color: #d39e00 !important;
                        border-color: transparent !important;
                        background: transparent !important;
                    `;
                    break;

                case 'Rejeitar todos':
                    btn.style.cssText = `
                        color: ${ACCESSIBLE_COLORS.buttonText} !important;
                        border: 2px solid ${ACCESSIBLE_COLORS.buttonBorder} !important;
                        background: transparent !important;
                    `;
                    break;

                case 'Aceitar todos':
                    btn.style.cssText = `
                        color: #1a1a1a !important;
                        background: #ffc107 !important;
                        border-color: #ffc107 !important;
                    `;
                    break;
            }

            if (btn.tagName === 'DIV' && !btn.dataset.kb) {
                btn.dataset.kb = '1';
                btn.onkeydown = (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        btn.click();
                    }
                };
            }
        });

        paper.onkeydown = (e) => {
            if (e.key === 'Tab') {
                const focusable = [...paper.querySelectorAll('button, [role="button"][tabindex="0"]')];
                const first = focusable[0];
                const last = focusable[focusable.length - 1];

                if (e.shiftKey && document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };

        const escHandler = (e) => {
            if (e.key === 'Escape' && paper.isConnected) {
                paper.querySelector('[role="button"]')?.click();
            }
        };
        document.addEventListener('keydown', escHandler);

        requestAnimationFrame(() => setTimeout(() => paper.focus(), 100));

        new MutationObserver(() => {
            if (!paper.isConnected) {
                document.removeEventListener('keydown', escHandler);
            }
        }).observe(document.body, { childList: true, subtree: true });
    },
});
